import { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';

const HOT_TTL_MS = Math.max(5000, Number(import.meta.env.VITE_PUBLIC_DATA_CACHE_MS || 30000));
const responseCache = new Map();
const inflightCache = new Map();

function stableStringify(value) {
  if (value == null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((x) => stableStringify(x)).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function readCached(key) {
  const entry = responseCache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    responseCache.delete(key);
    return undefined;
  }
  return entry.value;
}

async function fetchWithDedupedCache(key, fetcher, ttlMs = HOT_TTL_MS) {
  const cached = readCached(key);
  if (cached !== undefined) return cached;

  const inflight = inflightCache.get(key);
  if (inflight) return inflight;

  const p = Promise.resolve()
    .then(fetcher)
    .then((value) => {
      responseCache.set(key, {
        value,
        expiresAt: Date.now() + Math.max(0, ttlMs)
      });
      if (responseCache.size > 200) {
        const firstKey = responseCache.keys().next().value;
        if (firstKey) responseCache.delete(firstKey);
      }
      return value;
    })
    .finally(() => {
      inflightCache.delete(key);
    });
  inflightCache.set(key, p);
  return p;
}

export const useHeroSliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSliders = async () => {
      try {
        const key = 'content:hero-sliders';
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getHeroSliders(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        // Handle both response structures
        const slidersData = response.data?.data || response.data || [];
        setSliders(Array.isArray(slidersData) ? slidersData : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching hero sliders:', err);
        setError(err.message);
        setSliders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSliders();
    return () => {
      cancelled = true;
    };
  }, []);

  return { sliders, loading, error };
};

export const useEvents = (params = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      try {
        const key = `content:events:${stableStringify(params)}`;
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getEvents(params),
          HOT_TTL_MS
        );
        if (cancelled) return;
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];

        const filtered = rows.filter((row) => {
          // Keep public cards clean from inactive/cancelled/completed batches.
          if (row?.status === 'completed' || row?.status === 'cancelled' || row?.status === 'inactive') {
            return false;
          }
          // Defensive filtering in case backend param mapping changes.
          if ((params.featured || params.isHighlighted) && !row?.isHighlighted) return false;
          if ((params.special || params.isSpecial) && !row?.isSpecial) return false;
          return Boolean(row?.id) && Boolean(row?.title || row?.name);
        });

        // Remove duplicates (can happen when legacy + dynamic sources overlap).
        const uniqueBySlugOrId = new Map();
        filtered.forEach((row) => {
          const key = row.slug || row.id;
          if (!uniqueBySlugOrId.has(key)) uniqueBySlugOrId.set(key, row);
        });

        setEvents(Array.from(uniqueBySlugOrId.values()));
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(params)]);

  return { events, loading, error };
};

export const useHighlightedEvents = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchItems = async () => {
      try {
        const key = 'content:highlighted-events';
        const cached = readCached(key);
        setLoading(!cached);
        const res = await fetchWithDedupedCache(
          key,
          () => contentAPI.getHighlightedEvents(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setItems(rows);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchItems();
    return () => {
      cancelled = true;
    };
  }, []);

  return { highlightedEvents: items, loading, error };
};

export const useBlogs = (params = {}) => {
  const resolved =
    typeof params === 'number'
      ? { limit: params }
      : { limit: 50, publishedOnly: true, ...params };

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchBlogs = async () => {
      try {
        const key = `content:blogs:${stableStringify(resolved)}`;
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getBlogs(resolved),
          HOT_TTL_MS
        );
        if (cancelled) return;
        const rows = response.data?.data;
        setBlogs(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setBlogs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBlogs();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(resolved)]);

  return { blogs, loading, error };
};

export const useTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTestimonials = async () => {
      try {
        const key = 'content:testimonials';
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getTestimonials(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        const raw = response?.data?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.items)
            ? raw.items
            : [];
        setTestimonials(list);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTestimonials();
    return () => {
      cancelled = true;
    };
  }, []);

  return { testimonials, loading, error };
};

export const usePartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPartners = async () => {
      try {
        const key = 'content:partners';
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getPartners(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        setPartners(response.data.data);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPartners();
    return () => {
      cancelled = true;
    };
  }, []);

  return { partners, loading, error };
};

export const useStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const key = 'content:stats';
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getStats(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        setStats(response.data.data);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  return { stats, loading, error };
};

export const useLoginPageImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await contentAPI.getLoginPageImages();
        setImages(response.data.data || []);
      } catch (err) {
        console.error('Error fetching login page images:', err);
        setError(err.message);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  return { images, loading, error };
};

const DEFAULT_SITE_SETTINGS = {
  site_favicon_url: '',
  site_public_logo_url: '',
  site_header_logo_url: '',
  site_footer_logo_url: '',
  site_brand_name: 'INVINCIBLE',
  site_public_title: 'Invincible NGO | India\'s Largest Trekking Organization',
  site_meta_description:
    'Invincible NGO organizes affordable trekking and adventure camps across India — snow treks, weekend trips, and expeditions. 2,20,000+ participants, 2500+ volunteers, 11+ years.',
  site_public_url: 'https://digix7.com',
  site_admin_title: 'INVINCIBLE - Admin Panel',
  stats_section_title: "India's Largest Trekking Organization",
  stats_section_subtitle: 'Numbers That Tell Our Story',
  stats_section_image_url: '',
  highlighted_events_title: 'Highlighted Events',
  highlighted_events_subtitle: 'Recommended camps by our Instructors.',
  choose_experience_title: 'Choose Your Experience',
  choose_experience_subtitle: 'Pick The Perfect Adventure For You',
  why_section_title: 'Why Adventure Seeks Love Invincible',
  why_section_subtitle: 'What Makes Our Community Different',
  why_community_image_url: '',
  special_events_title: 'Special Events: A Once In A Lifetime Opportunity',
  special_events_subtitle: 'Unique Experiences Curated For The Bold',
  blogs_section_title: 'Latest Blogs',
  blogs_section_subtitle: 'Stories From The Trails',
  home_latest_blog_ids: '[]',
  partners_section_title: 'Our Partners & Associations',
  partners_section_subtitle: 'Trusted By Institutions & Organizations Across India',
  // Page heroes (list/collection pages)
  events_hero_title: 'Discover Your Next Adventure',
  events_hero_subtitle: 'Browse Through Our Curated List Of Treks And Expeditions.',
  events_hero_image: '',
  blogs_hero_title: 'Latest Blogs',
  blogs_hero_subtitle: 'Stories From The Trails',
  blogs_hero_image: '',
  about_hero_title: 'About Invincible',
  about_hero_subtitle: 'Adventure Awaits, Community Thrives',
  about_mission_title: 'Our Mission',
  about_mission_text: '',
  about_impact_title: 'Our Impact',
  about_what_we_do_title: 'What We Do',
  about_feature1_title: 'Weekend Events',
  about_feature1_desc: '',
  about_feature2_title: 'Snow Treks',
  about_feature2_desc: '',
  about_feature3_title: 'Road Trips',
  about_feature3_desc: '',
  about_feature4_title: 'Expeditions',
  about_feature4_desc: '',
  about_join_title: 'Join Our Community',
  about_join_text: '',
  contact_hero_title: 'Get in Touch',
  contact_hero_subtitle: "We'd love to hear from you",
  contact_address: '',
  contact_phone: '',
  contact_email: '',
  contact_hours: '',
  footer_mission: '',
  footer_copyright: '© 2024 Invincible. All rights reserved.',
  footer_events_categories: '',
  footer_quick_links: '',
  footer_regional_link_text: 'Contact For Rajkot, Baroda, Surat Office →',
  terms_page_title: 'Terms & Conditions',
  terms_page_html: '',
  privacy_page_title: 'Privacy Policy',
  privacy_page_html: '',
  initiatives_hero_title: 'Our Initiatives',
  initiatives_hero_subtitle: 'Making a positive impact',
  initiatives_cards: '',
  community_hero_title: 'Our Community',
  community_hero_subtitle: 'Stories from our adventure family',
  community_section_title: 'What Our Community Says',
  social_instagram: '',
  social_linkedin: '',
  social_facebook: '',
  social_whatsapp: '',
  social_youtube: '',
  careers_hero_title: 'Join The Team',
  careers_hero_subtitle: 'Be part of something extraordinary',
  careers_why_title: 'Why Join Invincible?',
  careers_positions_title: 'Open Positions',
  careers_apply_title: 'Apply Now',
  careers_positions_list: 'Adventure Guide, Event Coordinator, Marketing Specialist, Content Writer, Photographer, Operations Manager',
  careers_benefit1_title: 'Adventure Every Day',
  careers_benefit1_desc: '',
  careers_benefit2_title: 'Amazing Team',
  careers_benefit2_desc: '',
  careers_benefit3_title: 'Growth Opportunities',
  careers_benefit3_desc: '',
  careers_benefit4_title: 'Flexible Work',
  careers_benefit4_desc: '',
  event_consultant_card_title: 'Hey, Traveler!',
  event_consultant_card_subtitle:
    'Connect With Me If You Have Any Questions Regarding This Package.',
  event_consultant_fallback_team_name: 'Invincible Team',
  event_consultant_whatsapp_button_label: 'Whatsapp',
  event_consultant_pdf_button_label: 'Get PDF',
  event_consultant_callback_button_label: 'Request A Callback',
  event_callback_modal_title: 'Request A Callback',
  event_consultant_pdf_url: '',
  event_special_offer_title: 'Invincible Special 11',
  event_special_offer_body: 'Planning A Group Adventure? Book For 10 People And Get The 11th Spot Entirely Free! Perfect For College Batches And Large Friend Groups.',
  event_special_offer_enabled: 'true',
};

export const useSiteSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const key = 'content:site-settings';
        const cached = readCached(key);
        setLoading(!cached);
        const response = await fetchWithDedupedCache(
          key,
          () => contentAPI.getSiteSettings(),
          HOT_TTL_MS
        );
        if (cancelled) return;
        const data = response.data?.data || response.data || {};
        setSettings({ ...DEFAULT_SITE_SETTINGS, ...data });
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching site settings:', err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  return { settings, loading, error };
};
